import { DynamicScroller, DynamicScrollerItem, RecycleScroller } from 'vue-virtual-scroller';

export default {
  name: 'VirtualScrollNew',
  components: {
    DynamicScroller,
    DynamicScrollerItem,
    RecycleScroller,
  },
  props: {
    /*
      See all props in here:
      https://github.com/Akryum/vue-virtual-scroller/blob/master/packages/vue-virtual-scroller/README.md
    */
    items: {
      type: Array,
      required: true,
    },
    mode: {
      type: String,
      default: 'recycle',
      validator(value) {
        return [
          'recycle',
          'dynamic',
        ].includes(value);
      },
    },
    minItemSize: {
      type: Number,
      default: 44,
    },
    buffer: {
      type: Number,
      default: 80,
    },
    emitScroll: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      // Vue-virtual-scroller
      scroller: null,
      scrollerRecycle: null,
      scrollerDynamic: null,

      // ScrollArea
      scrollArea: 'VirtualScrollArea',
      scrollAreaContainer: null,
      scrollAreaViewport: null,

      // Container
      childElements: [],
      totalHeight: 0,

      // Items
      itemsNorm: [],
      visibleItems: [],
      hiddenStartItems: [],
      hiddenEndItems: [],
      startIndex: 0,
      endIndex: 0,
    };
  },
  computed: {
    count() {
      return this.itemsNorm.length;
    },
    visibleCount() {
      return this.visibleItems.length;
    },
    hiddenStartCount() {
      return this.hiddenStartItems.length;
    },
    hiddenEndCount() {
      return this.hiddenEndItems.length;
    },
    visibleStartIndex() {
      if (this.visibleItems.length) {
        return this.visibleItems[0].id;
      }
      return -1;
    },
    visibleEndIndex() {
      if (this.visibleItems.length) {
        const lastIndex = this.visibleItems.length - 1;
        return this.visibleItems[lastIndex].id;
      }
      return -1;
    },
  },
  watch: {
    totalHeight: {
      async handler(value) {
        this.setHeight(value);
      },
      immediate: true,
    },
    items(value) {
      this.scroller.handleResize();
    },
  },
  mounted() {
    this.init();
  },
  beforeMount() {
    if (this.scrollAreaContainer) {
      this.scrollAreaContainer.removeEventListener('scroll', this.updateScroll);
    }
  },
  methods: {
    init() {
      if (!this.$refs.virtualScroller) return;

      if (this.mode === 'recycle') {
        this.scrollerRecycle = this.$refs.virtualScroller;
        this.scroller = this.scrollerRecycle;
      }

      if (this.mode === 'dynamic') {
        this.scrollerDynamic = this.$refs.virtualScroller.$children[0];
        this.scroller = this.scrollerDynamic;
      }

      this.scroller.getScroll = this.getScroll;

      this.scrollAreaContainer = this.scroller.$el && this.scroller.$el.querySelector('.scrollarea__container');
      this.scrollAreaViewport = this.scroller.$el && this.scroller.$el.querySelector('.virtual-scroll-area__viewport');

      if (this.scrollAreaContainer) {
        this.scrollAreaContainer.addEventListener('scroll', this.updateScroll);
      }

      this.updateScroll();
    },
    update() {
      this.updateElements();
      this.updateScroll();

      this.$emit('virtual-scroll', {
        count: this.count,
        visibleCount: this.visibleCount,
        hiddenStartCount: this.hiddenStartCount,
        hiddenEndCount: this.hiddenEndCount,
        startIndex: this.startIndex,
        endIndex: this.endIndex,
        visibleStartIndex: this.visibleStartIndex,
        visibleEndIndex: this.visibleEndIndex,
      });
    },
    updateElements() {
      this.childElements = this.scrollAreaViewport && this.scrollAreaViewport.childNodes;

      this.itemsNorm = [];
      this.hiddenStartItems = [];
      this.visibleItems = [];
      this.hiddenEndItems = [];

      let isVisibleStart = false;

      this.childElements.forEach((item, index) => {
        const isValidNodeType = item && item.nodeName !== '#text';

        if (!item || !isValidNodeType) return;

        const style = window.getComputedStyle(item);
        const matrix = new DOMMatrixReadOnly(style.transform);
        const translateY = matrix.m42;

        const isItemOffscreen = translateY !== -9999;
        const isScrollerItem = item && item.className.search(/item-view/g);

        if (!isItemOffscreen || !isScrollerItem) return;

        this.itemsNorm.push({
          index,
          id: this.getItemIdAttribute(item.children[0]),
          element: item,
        });

        if (this.isVisible(item, this.$refs.virtualScroller.$el)) {
          isVisibleStart = true;
          this.setViewportAttribute(item, 'visible');
          this.visibleItems.push({
            index,
            id: this.getItemIdAttribute(item.children[0]),
            element: item,
          });
        } else {
          if (!isVisibleStart) {
            this.setViewportAttribute(item, 'hidden-start');
            this.hiddenStartItems.push({
              index,
              id: this.getItemIdAttribute(item.children[0]),
              element: item,
            });
          } else {
            this.setViewportAttribute(item, 'hidden-end');
            this.hiddenEndItems.push({
              index,
              id: this.getItemIdAttribute(item.children[0]),
              element: item,
            });
          }
        }
      });
    },
    updateScroller(startIndex, endIndex, _visibleStartIndex, _visibleEndIndex) {
      this.startIndex = startIndex;
      this.endIndex = endIndex - 1;

      this.update();
    },
    async updateScroll(event) {
      this.totalHeight = this.scroller.totalSize;

      this.scroller.handleScroll(event);
    },
    isVisible(targetElm, viewportElm) {
      if (!targetElm || !viewportElm) return;

      let targetRect;
      let viewportRect;

      if (typeof targetElm === 'object' && 'getBoundingClientRect' in targetElm) {
        targetRect = targetElm.getBoundingClientRect();
      }

      if (typeof viewportElm === 'object' && 'getBoundingClientRect' in viewportElm) {
        viewportRect = viewportElm.getBoundingClientRect();
      }

      if (!targetRect || !viewportRect) return;

      if (targetRect.top <= viewportRect.top) {
        const top = viewportRect.top - targetRect.top;
        return top <= targetRect.height;
      } else {
        const bottom = targetRect.bottom - viewportRect.bottom;
        return bottom <= targetRect.height;
      }
    },
    setViewportAttribute(elm, state) {
      if (elm && elm.dataset) elm.dataset.viewport = state;
    },
    getItemIdAttribute(elm) {
      return (elm && elm.dataset) ? parseInt(elm.dataset.index) : -1;
    },
    setHeight(value) {
      if (!this.scrollAreaViewport) return;
      this.scrollAreaViewport.style.height = `${value}px`;
    },
    getScroll() {
      return {
        start: this.scrollAreaContainer.scrollTop,
        end: this.scrollAreaContainer.scrollTop + this.scroller.$el.getBoundingClientRect().height,
      };
    },
  },
};
